import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@/assets/icons/ArrowRightIcon';
import { Button } from '@/components/ui/Button';
import { resourceFolderPath } from '@/lib/constants/routes';
import { resourceCoverUrl } from '../resource-cover';
import { RESOURCES_STRINGS } from '../constants/resources-strings';
import type { ResourceFolderDto } from '@/lib/types/resources.types';

interface FolderCardProps {
  folder: ResourceFolderDto;
}

/*
 * Same geometry as the events card's View Details button (EventCard.tsx). The
 * `!` utilities are load-bearing: they collide with the ones `Button`'s
 * `outline` variant emits, and Tailwind gives colliding utilities equal
 * specificity, so without `!` the winner depends on stylesheet emit order.
 * Copied rather than imported so EventCard stays untouched (design 02 s.28).
 */
const VIEW_DETAILS_CLASSES = 'h-9 rounded-lg! px-4! py-0! text-xs! flex items-center';

/**
 * One card in the "Browse by Category" grid (FR-178), in the anatomy of the
 * dashboard's event card: a clean 2:1 cover on top (A-191, nothing overlays
 * it), then the name on a row of its own, and below it the count as plain text
 * on the left with an outlined View Details button on the right.
 *
 * The cover, the name and the button each link to the folder page (A-192); the
 * article itself is not a link. The cover link is hidden from assistive tech
 * and the tab order so there is one stop per target (name, button), not three.
 * The cover falls back to a placeholder photo until an admin uploads one.
 */
export function FolderCard({ folder }: FolderCardProps) {
  const folderPath = resourceFolderPath(folder.slug);
  const coverUrl = resourceCoverUrl(folder.coverImageUrl);

  return (
    <article className="border-border-strong bg-surface-card flex flex-col overflow-hidden rounded-2xl border">
      <Link to={folderPath} aria-hidden="true" tabIndex={-1} className="block">
        <div className="bg-bg relative aspect-[2/1] w-full shrink-0">
          {coverUrl && <img src={coverUrl} alt="" className="absolute inset-0 size-full object-cover" />}
        </div>
      </Link>
      {/* Two rows, as on the events card: the name has the full width to itself
          so it is never squeezed by the button, and the count and button share
          the row below, the button always on the right. */}
      <div className="flex flex-col gap-3 p-4">
        <h3 className="text-text-primary line-clamp-2 text-[0.9375rem] leading-[1.375rem] font-semibold break-words">
          <Link
            to={folderPath}
            className="focus-visible:ring-green rounded-sm hover:underline focus-visible:ring-2 focus-visible:outline-none"
          >
            {folder.name}
          </Link>
        </h3>
        <div className="flex items-center justify-between gap-3">
          <p className="text-text-secondary text-base leading-[1.125rem]">
            {RESOURCES_STRINGS.FOLDER_ITEM_COUNT(folder.itemCount)}
          </p>
          <Button asChild variant="outline" className={VIEW_DETAILS_CLASSES}>
            <Link to={folderPath} aria-label={RESOURCES_STRINGS.FOLDER_VIEW_DETAILS_LABEL(folder.name)}>
              <span className="inline-flex items-center gap-2">
                {RESOURCES_STRINGS.VIEW_DETAILS}
                <ArrowRightIcon size={9} />
              </span>
            </Link>
          </Button>
        </div>
      </div>
    </article>
  );
}
